/* Admin: Donor leads list + detail. Leads can be event-scoped. */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Link, navigate } from "../../lib/router-compat.jsx";
import AdminShell from "../../components/shell/AdminShell.jsx";
import { Button, Card, Badge, Select, EmptyState, Skeleton, ConfirmDialog, Icon, showToast } from "../../ui/index.js";
import { useLeads, useDonations } from "../../data/hooks.js";
import { updateLeadStatus } from "../../data/mutations.js";
import { deleteDoc, doc } from "firebase/firestore";
import { db } from "../../lib/firebase.js";
import { COL } from "../../data/collections.js";
import { formatRelative, formatDateTime, CURRENCY_FMT } from "../../utils/format.js";

const methodLabel = (m) => (m === "either" ? "Either" : m === "momo" ? "Mobile money" : "Bank transfer");

export function AdminLeadsListPage() {
  const { leads, loading } = useLeads();
  const [filter, setFilter] = useState("all");
  const filtered = leads.filter((l) => (filter === "all" ? true : l.status === filter));

  return (
    <AdminShell>
      <div className="admin-main__header">
        <div><h1 className="t-3xl">Donor leads</h1><p>People who submitted the donate or event pledge form. Triage and follow up.</p></div>
      </div>
      <div className="chips" style={{ marginBottom: 24 }}>
        {[["all", "All"], ["new", "New"], ["contacted", "Contacted"], ["fulfilled", "Fulfilled"]].map(([k, label]) => (
          <button key={k} className={`chip ${filter === k ? "active" : ""}`} onClick={() => setFilter(k)}>{label} ({k === "all" ? leads.length : leads.filter((l) => l.status === k).length})</button>
        ))}
      </div>
      {loading ? <Skeleton h={240} /> : filtered.length === 0 ? (
        <Card><EmptyState icon={<Icon.Users size={40} />} title={leads.length === 0 ? "No donor leads yet." : "No leads match this filter."} body={leads.length === 0 ? "They'll appear here when supporters submit the donate form." : "Try a different filter."} /></Card>
      ) : (
        <table className="table">
          <thead><tr><th>Name</th><th>Email</th><th style={{ width: 150 }}>Amount</th><th style={{ width: 140 }}>Event</th><th style={{ width: 120 }}>Status</th><th style={{ width: 130 }}>Submitted</th><th className="table__actions">Actions</th></tr></thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="clickable" onClick={() => navigate(`/admin/leads/${l.id}`)}>
                <td style={{ fontWeight: 600 }}>{l.donorName}</td>
                <td className="t-sm muted">{l.donorEmail}</td>
                <td>{CURRENCY_FMT(l.intendedAmount, l.currency)}</td>
                <td className="t-sm">{l.eventId || <span className="muted">General</span>}</td>
                <td><Badge variant={l.status}>{l.status[0].toUpperCase() + l.status.slice(1)}</Badge></td>
                <td className="t-sm muted">{formatRelative(l.submittedAt)}</td>
                <td className="table__actions" onClick={(e) => e.stopPropagation()}><Button variant="ghost" size="sm" onClick={() => navigate(`/admin/leads/${l.id}`)}>View</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </AdminShell>
  );
}

function DetailRow({ label, value, bold }) {
  return (
    <div>
      <div className="t-xs muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{label}</div>
      <div className="t-base" style={{ fontWeight: bold ? 700 : 500 }}>{value}</div>
    </div>
  );
}

export function AdminLeadDetailPage() {
  const { leadId } = useParams();
  const { leads, loading } = useLeads();
  const { donations } = useDonations();
  const [showDelete, setShowDelete] = useState(false);

  if (loading) return <AdminShell><Skeleton h={300} /></AdminShell>;
  const lead = leads.find((l) => l.id === leadId);
  if (!lead) return <AdminShell><EmptyState title="Lead not found" body="It may have been deleted." /></AdminShell>;
  const linkedDonation = lead.linkedDonationId ? donations.find((d) => d.id === lead.linkedDonationId) : null;

  const updateStatus = async (status) => {
    try { await updateLeadStatus(lead.id, status); showToast({ intent: "success", title: "Status updated" }); }
    catch (err) { showToast({ intent: "danger", title: "Couldn't update", body: err.message }); }
  };
  const doDelete = async () => {
    try { await deleteDoc(doc(db, COL.donorLeads, lead.id)); showToast({ intent: "success", title: "Lead deleted" }); navigate("/admin/leads"); }
    catch (err) { showToast({ intent: "danger", title: "Couldn't delete", body: err.message }); }
  };

  return (
    <AdminShell>
      <Link to="/admin/leads" className="back-link"><Icon.ArrowLeft size={14} />Back to leads</Link>
      <div className="row" style={{ marginTop: 16, marginBottom: 32, gap: 16 }}>
        <h1 className="t-3xl" style={{ margin: 0, flex: 1 }}>Lead from {lead.donorName}</h1>
        <Badge variant={lead.status}>{lead.status[0].toUpperCase() + lead.status.slice(1)}</Badge>
      </div>

      <div className="dashboard-split" style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 24 }}>
        <div className="stack-lg" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card>
            <h2 className="t-2xl" style={{ marginTop: 0, marginBottom: 20 }}>Donor details</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <DetailRow label="Name" value={lead.donorName} />
              <DetailRow label="Email" value={<a href={`mailto:${lead.donorEmail}`}>{lead.donorEmail}</a>} />
              <DetailRow label="Phone" value={lead.donorPhone || "—"} />
              <DetailRow label="Country" value={lead.country || "—"} />
              <DetailRow label="Organisation" value={lead.organization || "—"} />
              <DetailRow label="Intended amount" value={CURRENCY_FMT(lead.intendedAmount, lead.currency)} bold />
              <DetailRow label="Preferred method" value={methodLabel(lead.method)} />
              <DetailRow label="For event" value={lead.eventId || "General fund"} />
            </div>
            {lead.message && (
              <div style={{ marginTop: 24, padding: 16, background: "var(--bg-warm)", borderRadius: "var(--radius-md)" }}>
                <div className="t-xs muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Message</div>
                <div className="t-base" style={{ fontStyle: "italic" }}>"{lead.message}"</div>
              </div>
            )}
          </Card>
          <Card>
            <h2 className="t-2xl" style={{ marginTop: 0, marginBottom: 16 }}>Submission</h2>
            <DetailRow label="Submitted at" value={formatDateTime(lead.submittedAt)} />
          </Card>
        </div>

        <div className="stack-lg" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card>
            <h3 className="t-base" style={{ margin: 0, marginBottom: 8, fontWeight: 600 }}>Status</h3>
            <p className="t-sm muted" style={{ margin: "0 0 12px" }}>Move through the funnel as you follow up.</p>
            <Select value={lead.status} onChange={(e) => updateStatus(e.target.value)}>
              <option value="new">New</option><option value="contacted">Contacted</option><option value="fulfilled">Fulfilled</option>
            </Select>
          </Card>
          <Card>
            <h3 className="t-base" style={{ margin: 0, marginBottom: 12, fontWeight: 600 }}>Donation</h3>
            {linkedDonation ? (
              <div>
                <div className="t-base" style={{ fontWeight: 600 }}>{CURRENCY_FMT(linkedDonation.amount, linkedDonation.currency)}</div>
                <div className="t-sm muted" style={{ marginTop: 4 }}>{linkedDonation.method} · received {linkedDonation.receivedDate}</div>
                <Link to="/admin/donations" className="t-sm" style={{ display: "inline-flex", marginTop: 12, alignItems: "center", gap: 4, fontWeight: 500 }}>View donation <Icon.ArrowRight size={12} /></Link>
              </div>
            ) : (
              <div>
                <p className="t-sm muted" style={{ margin: "0 0 12px" }}>No donation logged yet.</p>
                <Button variant="primary" size="sm" onClick={() => navigate("/admin/donations?prefill=" + lead.id)}>Log donation from this lead</Button>
              </div>
            )}
          </Card>
          <Card>
            <h3 className="t-base" style={{ margin: 0, marginBottom: 12, fontWeight: 600 }}>Other actions</h3>
            <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <a href={`mailto:${lead.donorEmail}`} className="btn btn--secondary"><Icon.Send size={14} />Email this donor</a>
              <Button variant="ghost" onClick={() => setShowDelete(true)} style={{ color: "var(--danger)" }}><Icon.Trash size={14} />Delete this lead</Button>
            </div>
          </Card>
        </div>
      </div>

      <ConfirmDialog open={showDelete} title="Delete this lead?" body="The lead will be permanently removed." confirmLabel="Delete" destructive onConfirm={doDelete} onClose={() => setShowDelete(false)} />
    </AdminShell>
  );
}

export default AdminLeadsListPage;
